import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlass, PlusCircle } from 'phosphor-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../../../shared/PageLayout';
import { InputComponent } from '../../../../shared/InputComponent';
import styles from './budget-list.module.css';
import { Button } from '../../../../shared/Button';
import { BudgetTable } from './BudgetTable';
import { FeedbackModal } from '../../../../shared/FeedbackModal';
import useRemoveModal from './hooks/useRemoveModal';
import { DeleteModal } from './components/DeleteModal';
import useSelectedBudget from './hooks/useSelectedBudget';
import { useBudgetData } from './hooks/useBudgetData';

export const BudgetList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const containerRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  const [tableHeight, setTableHeight] = useState(400);

  const removeModal = useRemoveModal();
  const budgetSelection = useSelectedBudget();

  const {
    budgets,
    filteredBudgets,
    deleteRepair,
    searchQuery,
    setSearchQuery,
    isFetchingRepairs,
  } = useBudgetData();

  const onDelete = useCallback(async () => {
    await deleteRepair(budgetSelection.selectedBudget?.id_budget ?? '');
    setSearchQuery('');
  }, [budgetSelection.selectedBudget?.id_budget, deleteRepair, setSearchQuery]);

  useEffect(() => {
    const updateTableHeight = () => {
      const containerHeight = containerRef.current?.offsetHeight ?? 0;
      const actionsHeight = actionsRef.current?.offsetHeight ?? 0;

      setTableHeight(containerHeight - actionsHeight);
    };

    updateTableHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateTableHeight();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    if (actionsRef.current) {
      resizeObserver.observe(actionsRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Atualiza a URL com a query de pesquisa
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (searchQuery) {
      params.set('q', searchQuery);
    } else {
      params.delete('q');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [location.search, navigate, searchQuery]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    setSearchQuery(query.toUpperCase());
  }, []); // carrega a query da URL apenas uma vez no início

  return (
    <PageLayout.Root title="ORÇAMENTOS">
      <FeedbackModal isOpen={isFetchingRepairs} variant="sending" />

      {removeModal.isOpen && (
        <DeleteModal
          onDelete={onDelete}
          onClose={() => {
            removeModal.onClose();
          }}
          budget={budgetSelection.selectedBudget}
        />
      )}

      <section style={{ height: '100%' }}>
        <div className={styles['budget-list']} ref={containerRef}>
          <div className={styles['budget-list-header']}>
            <div className={styles.actions} ref={actionsRef}>
              <InputComponent
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value.toUpperCase());
                }}
                placeholder="Pesquisar por Orçamento, Cliente, ou Placa"
                icon={
                  <button
                    type="button"
                    // onClick={() => {
                    //   refetchFilteredSearch();
                    // }}
                  >
                    <MagnifyingGlass
                      size="1.6rem"
                      color="#A5A5A5"
                      weight="bold"
                      className=""
                    />
                  </button>
                }
                className={styles.search}
              />
              <div className={styles.button}>
                <Button
                  handleClick={() => {
                    navigate(`/new-budget-form`);
                  }}
                  style={{
                    fontWeight: 700,
                    padding: '1rem',
                    boxShadow: '0px 2px 2px #00000044',
                    fontSize: '1.2rem',
                  }}
                >
                  <PlusCircle size="2rem" /> Novo Orçamento
                </Button>
              </div>
            </div>
          </div>
          <BudgetTable
            budgets={searchQuery.trim().length > 0 ? filteredBudgets : budgets}
            height={tableHeight ?? 400}
            isLoading={false}
          />
        </div>
      </section>
    </PageLayout.Root>
  );
};
